import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { NAV } from "@/constants/testIds";
import { Menu, X } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Navbar() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const signedIn = user && typeof user === "object";

  const linkCls = (path) =>
    `text-sm font-medium transition-colors duration-200 ${
      location.pathname.startsWith(path)
        ? "text-blue-800"
        : "text-slate-600 hover:text-blue-800"
    }`;

  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-slate-200/60">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            data-testid={NAV.logoLink}
            className="flex items-center gap-3"
          >
            <img
              src="https://customer-assets-7cd3h4nn.emergentagent.net/job_wealth-academy-46/artifacts/x15oz6g3_Peak%20Market%20Society%20Logo.png"
              alt="Peak Market Society"
              className="w-10 h-10 rounded-full object-contain bg-white ring-1 ring-slate-900/10"
            />
            <div className="flex flex-col leading-none">
              <span className="font-display font-black text-slate-900 tracking-tight text-lg">
                Peak Market Society
              </span>
              <span className="text-[10px] uppercase tracking-[0.22em] text-amber-600 font-semibold">
                {t("nav.tagline")}
              </span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            <Link to="/preview" data-testid="nav-preview-link" className={linkCls("/preview")}>
              {t("nav.preview")}
            </Link>
            <Link to="/pricing" data-testid={NAV.pricingLink} className={linkCls("/pricing")}>
              {t("nav.pricing")}
            </Link>
            <Link to="/residency" data-testid="nav-residency-link" className={linkCls("/residency")}>
              {t("nav.residency")}
            </Link>
            {signedIn && (
              <Link to="/dashboard" data-testid={NAV.dashboardLink} className={linkCls("/dashboard")}>
                {t("nav.dashboard")}
              </Link>
            )}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <LanguageSwitcher />
            {signedIn ? (
              <>
                <Link
                  to="/account"
                  data-testid={NAV.accountLink}
                  className="text-sm text-slate-600 hover:text-blue-800"
                >
                  {user.name || user.email}
                </Link>
                <Button
                  data-testid={NAV.logoutButton}
                  variant="outline"
                  onClick={async () => { await logout(); navigate("/"); }}
                  className="border-slate-300 hover:border-slate-900"
                >
                  {t("nav.logOut")}
                </Button>
              </>
            ) : (
              <>
                <Button
                  data-testid={NAV.loginButton}
                  variant="ghost"
                  onClick={() => navigate("/login")}
                  className="hover:bg-blue-50 hover:text-blue-800"
                >
                  {t("nav.logIn")}
                </Button>
                <Button
                  data-testid={NAV.registerButton}
                  onClick={() => navigate("/register")}
                  className="bg-blue-800 hover:bg-blue-700 text-white"
                >
                  {t("nav.getStarted")}
                </Button>
              </>
            )}
          </div>

          <div className="md:hidden flex items-center gap-1.5">
            <LanguageSwitcher compact prominent testId="nav-mobile-language-selector" />
            <button
              className="p-2 text-slate-700"
              data-testid={NAV.mobileToggle}
              onClick={() => setOpen(!open)}
              aria-label={t("nav.toggleMenu")}
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="md:hidden border-t border-slate-200 py-4 flex flex-col gap-3">
            <Link to="/preview" onClick={() => setOpen(false)} className={linkCls("/preview")}>{t("nav.preview")}</Link>
            <Link to="/pricing" onClick={() => setOpen(false)} className={linkCls("/pricing")}>{t("nav.pricing")}</Link>
            <Link to="/residency" onClick={() => setOpen(false)} className={linkCls("/residency")}>{t("nav.residency")}</Link>
            {signedIn ? (
              <>
                <Link to="/dashboard" onClick={() => setOpen(false)} className={linkCls("/dashboard")}>{t("nav.dashboard")}</Link>
                <Link to="/account" onClick={() => setOpen(false)} className={linkCls("/account")}>{t("nav.account")}</Link>
                <button
                  className="text-left text-slate-600 hover:text-blue-800"
                  onClick={async () => { await logout(); setOpen(false); navigate("/"); }}
                >
                  {t("nav.logOut")}
                </button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className={linkCls("/login")}>{t("nav.logIn")}</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="text-blue-800 font-semibold">
                  {t("nav.getStarted")} →
                </Link>
              </>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
